import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Home, User, Briefcase, Info, Mail } from 'lucide-react';
import Header from '../components/Header';
import ParticipationButton from '../components/ParticipationButton';

// Lecture tolérante : cette page lit l'API en fetch brut (champs parfois en snake_case).
const getInfo = (t) => t?.detailedInfo || t?.detailed_info || {};
const getSeriesId = (t) => {
  const info = getInfo(t);
  return info.seriesId ?? info.series_id ?? null;
};
const getDateStr = (t) => String(t?.date || t?.tournamentDate || t?.tournament_date || '').slice(0, 10);
const getTimeStr = (t) => String(t?.time || t?.tournamentTime || t?.tournament_time || '').slice(0, 5);

const SubEventDetailPage = () => {
  const { eventId, subEventId } = useParams();
  const navigate = useNavigate();

  // ✅ MODIFIÉ : Charger depuis l'API
  const [festival, setFestival] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ NOUVEAU : Charger le festival et le tournoi depuis l'API
  useEffect(() => {
    const loadTournament = async () => {
      try {
        setLoading(true);

        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';
        const token = localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

        // Charger le festival avec ses tournois
        const festivalResponse = await fetch(`${API_URL}/festivals/${eventId}`, {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });

        if (!festivalResponse.ok) {
          throw new Error('Festival non trouvé');
        }

        const festivalData = await festivalResponse.json();
        const festivalObj = festivalData.data || festivalData;

        setFestival(festivalObj);

        // Trouver le tournoi dans la liste
        const tournaments = festivalObj.tournaments || festivalObj.subEvents || [];
        const foundTournament = tournaments.find(t => t.id === parseInt(subEventId));

        if (!foundTournament) {
          throw new Error('Tournoi non trouvé');
        }

        setTournament(foundTournament);
      } catch (err) {
        console.error('Erreur chargement:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadTournament();
  }, [eventId, subEventId]);

  // ✅ NOUVEAU : Loader
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-gray-600">Chargement...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ✅ NOUVEAU : Erreur
  if (error || !festival || !tournament) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">
            {error || 'Événement non trouvé'}
          </h2>
          <button
            onClick={() => navigate('/calendar')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg"
          >
            Retour au calendrier
          </button>
        </div>
      </div>
    );
  }

  // Préparer les données du tournoi
  const detailedInfo = tournament.detailedInfo || tournament.detailed_info || {};

  // Programme de la série : tous les jours/flights du même groupe, triés par date et heure.
  // Il remplace le programme saisi à la main quand la série compte plusieurs tournois.
  const currentSeriesId = getSeriesId(tournament);
  const seriesProgram = currentSeriesId == null
    ? []
    : (festival.tournaments || festival.subEvents || [])
        .filter((t) => getSeriesId(t) === currentSeriesId)
        .sort((a, b) =>
          (getDateStr(a) + ' ' + getTimeStr(a)).localeCompare(getDateStr(b) + ' ' + getTimeStr(b)));
  const seriesName = detailedInfo.seriesName ?? detailedInfo.series_name ?? null;
  const showSeriesProgram = seriesProgram.length > 1;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <div className="mb-6 flex items-center text-sm text-gray-600">
            <button
              onClick={() => navigate('/calendar')}
              className="hover:text-blue-600"
            >
              Calendrier
            </button>
            <span className="mx-2">/</span>
            <button
              onClick={() => navigate('/calendar')}
              className="hover:text-blue-600"
            >
              {festival.title}
            </button>
            <span className="mx-2">/</span>
            <span className="text-gray-800 font-semibold">{tournament.title}</span>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            {/* Header with color */}
            {/* La couleur du circuit est une classe Tailwind (ex. "bg-red-700"), pas
                une couleur CSS : l'utiliser en style inline donnait un fond
                transparent, donc titre et description blancs sur blanc. */}
            <div className={`text-white p-4 sm:p-6 ${festival.circuit?.color || 'bg-blue-600'}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="flex-1 min-w-0 flex items-start gap-4">
                  {festival.circuit?.logoUrl && (
                    <img
                      src={festival.circuit.logoUrl}
                      alt="Logo circuit"
                      className="w-16 h-16 flex-shrink-0 object-contain bg-white rounded-lg p-2"
                    />
                  )}
                  <div>
                    {festival.circuit?.acronym && (
                      <div className="mb-2">
                        <span className="bg-white bg-opacity-20 text-white text-xs font-semibold px-3 py-1 rounded-full">
                          {festival.circuit.acronym} - {festival.title}
                        </span>
                      </div>
                    )}
                    <h1 className="text-2xl sm:text-3xl font-bold mb-2 break-words">{tournament.title}</h1>
                    <p className="text-base sm:text-lg opacity-90">{tournament.description}</p>
                  </div>
                </div>

                {/* ✅ MODIFIÉ : Utiliser le composant ParticipationButton */}
                <div className="sm:ml-4 flex-shrink-0">
                  <ParticipationButton
                    tournamentId={tournament.id}
                    registrationDisabled={detailedInfo.hasOwnBuyIn === false}
                    onParticipationChange={(participating) => {
                      // Mettre à jour l'état local si nécessaire
                      setTournament(prev => ({
                        ...prev,
                        participating
                      }));
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Event Information */}
            <div className="p-6 space-y-6">
              {/* Date and Time */}
              <div className="flex items-start gap-4">
                <Calendar className="text-blue-600 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-1">Date et heure</h3>
                  <p className="text-gray-700">
                    {new Date(tournament.date || tournament.tournamentDate).toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </p>
                  <p className="text-gray-600">
                    {(tournament.time || tournament.tournamentTime || '').toString().slice(0, 5)}
                  </p>
                </div>
              </div>

              {/* Location */}
              {/* Lieu structuré (table venues) en priorité, texte libre en repli :
                  un tournoi importé ou créé avec un lieu n'a pas de texte libre. */}
              {(detailedInfo.venue?.name || detailedInfo.location) && (
                <div className="flex items-start gap-4">
                  <Home className="text-blue-600 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Lieu</h3>
                    <p className="text-gray-700">{detailedInfo.venue?.name || detailedInfo.location}</p>
                    {detailedInfo.venue && (detailedInfo.venue.address || detailedInfo.venue.city) && (
                      <p className="text-gray-500 text-sm">
                        {[
                          detailedInfo.venue.address,
                          [detailedInfo.venue.postal_code, detailedInfo.venue.city].filter(Boolean).join(' '),
                          detailedInfo.venue.country,
                        ].filter(Boolean).join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Organizer */}
              {detailedInfo.organizer && (
                <div className="flex items-start gap-4">
                  <User className="text-blue-600 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Organisateur</h3>
                    <p className="text-gray-700">{detailedInfo.organizer}</p>
                  </div>
                </div>
              )}

              {/* Tournament Details */}
              {(detailedInfo.buyIn || detailedInfo.prizePool || detailedInfo.startingStack || detailedInfo.blindStructure) && (
                <div className="flex items-start gap-4">
                  <Briefcase className="text-blue-600 mt-1" size={24} />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-3">Détails du tournoi</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {detailedInfo.buyIn && (
                        <div>
                          <p className="text-sm text-gray-600">Buy-in</p>
                          <p className="font-medium text-gray-800">{detailedInfo.buyIn}</p>
                        </div>
                      )}
                      {detailedInfo.prizePool && (
                        <div>
                          <p className="text-sm text-gray-600">Prize Pool</p>
                          <p className="font-medium text-gray-800">{detailedInfo.prizePool}</p>
                        </div>
                      )}
                      {detailedInfo.startingStack && (
                        <div>
                          <p className="text-sm text-gray-600">Stack de départ</p>
                          <p className="font-medium text-gray-800">{detailedInfo.startingStack}</p>
                        </div>
                      )}
                      {detailedInfo.blindStructure && (
                        <div>
                          <p className="text-sm text-gray-600">Structure des blinds</p>
                          <p className="font-medium text-gray-800">{detailedInfo.blindStructure}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Duration */}
              {detailedInfo.duration && (
                <div className="flex items-start gap-4">
                  <Info className="text-blue-600 mt-1" size={24} />
                  <div>
                    <h3 className="font-semibold text-lg mb-1">Durée</h3>
                    <p className="text-gray-700">{detailedInfo.duration}</p>
                  </div>
                </div>
              )}

              {/* Programme : celui de la série s'il y en a une, sinon le programme manuel */}
              {showSeriesProgram ? (
                <div className="flex items-start gap-4">
                  <Calendar className="text-blue-600 mt-1" size={24} />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg mb-1">Programme</h3>
                    {seriesName && (
                      <p className="text-sm text-gray-500 mb-3">Série : {seriesName}</p>
                    )}
                    <ul className="space-y-2">
                      {seriesProgram.map((t) => {
                        const isCurrent = t.id === tournament.id;
                        const dateStr = getDateStr(t);
                        const dateLabel = dateStr
                          ? new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
                              weekday: 'short', day: 'numeric', month: 'short'
                            })
                          : '';
                        return (
                          <li key={t.id}>
                            <button
                              type="button"
                              disabled={isCurrent}
                              onClick={() => navigate(`/event/${eventId}/subevent/${t.id}`)}
                              className={`w-full text-left rounded-lg border px-3 py-2 flex flex-wrap items-baseline gap-x-3 ${
                                isCurrent
                                  ? 'bg-blue-50 border-blue-300 cursor-default'
                                  : 'bg-white border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              <span className="font-medium text-gray-800 capitalize whitespace-nowrap">
                                {dateLabel}{getTimeStr(t) ? ` · ${getTimeStr(t)}` : ''}
                              </span>
                              <span className="text-gray-700 break-words">{t.title}</span>
                              {isCurrent && (
                                <span className="text-xs text-blue-700 font-semibold">(ce tournoi)</span>
                              )}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ) : (
                detailedInfo.agenda && detailedInfo.agenda.length > 0 && (
                  <div className="flex items-start gap-4">
                    <Calendar className="text-blue-600 mt-1" size={24} />
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg mb-3">Programme</h3>
                      <ul className="space-y-2">
                        {detailedInfo.agenda.map((item, index) => (
                          <li key={index} className="flex items-start">
                            <span className="text-blue-600 mr-2">•</span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )
              )}

              {/* Notes */}
              {detailedInfo.notes && (
                <div className="flex items-start gap-4">
                  <Info className="text-blue-600 mt-1" size={24} />
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg mb-1">Notes</h3>
                    <p className="text-gray-700 whitespace-pre-line">{detailedInfo.notes}</p>
                  </div>
                </div>
              )}

              {/* Contact */}
              <div className="flex items-start gap-4">
                <Mail className="text-blue-600 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-lg mb-1">Contact</h3>
                  <p className="text-gray-700">
                    Pour plus d'informations, veuillez nous contacter.
                  </p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="p-6 bg-gray-50 border-t border-gray-200 flex gap-4">
              <button
                onClick={() => navigate('/calendar')}
                className="flex-1 bg-gray-200 text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-300 transition"
              >
                Retour au calendrier
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubEventDetailPage;